import { Heart } from "lucide-react";
import type { CommentBubble } from "../../hooks/use-live-reactions";

/**
 * Live comment bubbles that drift up on the left, linger, then fade away.
 * Sized in vmin / clamp() so the text is legible from across a projected hall.
 * Premium frosted-glass card with a rosegold ring avatar and a heart accent.
 * Each bubble removes itself when its `ss-bubble-life` animation ends.
 */
export function CommentBubbles({
  bubbles,
  onDone,
}: {
  bubbles: CommentBubble[];
  onDone: (id: number) => void;
}) {
  return (
    <div className="pointer-events-none absolute left-[3vmin] bottom-[30%] z-30 flex flex-col gap-[1.4vmin] max-w-[min(82vw,60vmin)]">
      {bubbles.map((b) => (
        <div
          key={b.id}
          className="ss-bubble flex items-start gap-[1.6vmin] rounded-[1.8vmin] bg-white/12 backdrop-blur-xl border border-white/20 shadow-[0_1vmin_4vmin_rgba(0,0,0,0.5)]"
          style={{ ["--life" as string]: `${b.life}s`, padding: "1.7vmin 2.1vmin" } as React.CSSProperties}
          onAnimationEnd={() => onDone(b.id)}
        >
          <span
            className="shrink-0 rounded-full bg-gradient-to-br from-primary to-rose-400 text-white flex items-center justify-center font-bold border border-white/30 ring-2 ring-rose-300/40 select-none"
            style={{
              width: "clamp(2.75rem, 6.2vmin, 5.25rem)",
              height: "clamp(2.75rem, 6.2vmin, 5.25rem)",
              fontSize: "clamp(1rem, 2.5vmin, 2.1rem)",
            }}
          >
            {b.name[0]?.toUpperCase() ?? "?"}
          </span>
          <div className="min-w-0">
            <p
              className="flex items-center gap-2 font-bold text-white leading-tight"
              style={{ fontSize: "clamp(1.2rem, 2.8vmin, 2.7rem)" }}
            >
              <span className="truncate">{b.name}</span>
              <Heart
                className="shrink-0 text-rose-400"
                style={{ width: "0.85em", height: "0.85em" }}
                fill="currentColor"
                strokeWidth={0}
              />
            </p>
            <p
              className="text-white/90 leading-snug break-words line-clamp-3 mt-[0.6vmin]"
              style={{ fontSize: "clamp(1.05rem, 2.3vmin, 2.2rem)" }}
            >
              {b.text}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
